/**
 * Exclusão de produtos — o único caminho seguro quando existem VARIAÇÕES.
 *
 * POR QUE ISTO EXISTE (a armadilha nº 1 desta feature):
 * `products.parent_product_id` é ON DELETE CASCADE, então apagar o pai apaga as
 * filhas. Só que `quote_items.product_id` é ON DELETE **NO ACTION** (diferente
 * de `service_order_items`, que é SET NULL, e de `stock_movements`, que é
 * CASCADE). Se um `quote_items` ainda apontar para uma FILHA, o cascade estoura
 * violação de FK e a exclusão falha — ou pior, some silenciosamente num
 * `.update()` que não pegou os ids certos.
 *
 * Portanto: todo unlink de `quote_items` precisa incluir os ids das filhas.
 * Todas as exclusões de catálogo (individual, em massa e o executor da fila
 * offline) passam por aqui para que essa regra viva num lugar só.
 */
import { supabase } from '@/integrations/supabase/client';

/** Ids que ainda não existem no Postgres não entram em consulta nenhuma. */
function realIds(ids: string[]): string[] {
  return Array.from(new Set((ids ?? []).map(String).filter((id) => id && !id.startsWith('offline_'))));
}

/**
 * Busca no BANCO os ids das variações destes produtos. Consulta o servidor de
 * propósito: o cache local pode estar desatualizado, e um id de filha que
 * escape aqui vira erro de FK no delete.
 */
export async function fetchVariantIds(parentIds: string[]): Promise<string[]> {
  const parents = realIds(parentIds);
  if (parents.length === 0) return [];

  const { data, error } = await supabase
    .from('products')
    .select('id')
    .in('parent_product_id', parents);

  if (error) throw error;
  return (data ?? []).map((row: { id: string }) => String(row.id));
}

/**
 * Exclui os produtos informados JUNTO com todas as suas variações, desvinculando
 * antes os `quote_items` de pais e filhas.
 *
 * Devolve `{ error }` no mesmo formato do supabase para o chamador tratar o
 * toast, e `deletedIds` (pais + filhas) para o chamador limpar o cache local.
 */
export async function deleteProductsWithVariants(
  ids: string[],
): Promise<{ error: unknown | null; deletedIds: string[] }> {
  const parents = realIds(ids);
  if (parents.length === 0) return { error: null, deletedIds: [] };

  let allIds = parents;
  try {
    const childIds = await fetchVariantIds(parents);
    allIds = Array.from(new Set([...parents, ...childIds]));
  } catch (e) {
    // Sem a lista de filhas o delete abaixo pode estourar FK. Falhar aqui é
    // melhor do que apagar pela metade.
    return { error: e, deletedIds: [] };
  }

  // Desvincula o produto (e cada variação) dos orçamentos que o referenciam.
  const { error: unlinkError } = await supabase
    .from('quote_items')
    .update({ product_id: null })
    .in('product_id', allIds);

  if (unlinkError) return { error: unlinkError, deletedIds: [] };

  // `service_order_items.product_id` é SET NULL e `stock_movements.product_id`
  // é CASCADE — o banco cuida desses dois sozinho.
  const { error } = await supabase.from('products').delete().in('id', allIds);

  return { error: error ?? null, deletedIds: error ? [] : allIds };
}
